import React, { useState, useEffect } from 'react';
import { Row, Col, OverlayTrigger, Tooltip } from 'react-bootstrap';
import DataTable from 'react-data-table-component';
import { useNavigate } from 'react-router-dom';
import Instance from '../../Utils/Axios';
import PremiumLoader from '../Loader/PremiumLoader';
import CustomPagination from '../CustomPagination/CustomPagination';
import Fillbutton from '../FillButtonNew/Fillbutton';
import Select from 'react-select';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowDown, faArrowUp } from '@fortawesome/free-solid-svg-icons';
import './PackageDetails.css';
import useKeyScroll from "../../Hooks/useKeyScroll";

const TruncatedPackageName = ({ name, id, maxLines = 3, isTitle = false }) => {
  const textRef = React.useRef(null);
  const [isTruncated, setIsTruncated] = React.useState(false);

  React.useEffect(() => {
    const handleResize = () => {
      if (textRef.current) {
        setIsTruncated(textRef.current.scrollHeight > textRef.current.clientHeight);
      }
    };
    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [name]);

  const content = (
    <div
      ref={textRef}
      className={isTitle ? "package-details-expiry-title-label mb-0 w-100" : "w-100 m-0 p-0"}
      style={{
        whiteSpace: 'normal',
        wordBreak: 'break-word',
        display: '-webkit-box',
        WebkitLineClamp: maxLines,
        WebkitBoxOrient: 'vertical',
        overflow: 'hidden',
        cursor: isTruncated ? 'pointer' : 'default'
      }}
    >
      {name}
    </div>
  );

  return isTruncated ? (
    <OverlayTrigger placement="top" overlay={<Tooltip id={id}>{name}</Tooltip>}>
      {content}
    </OverlayTrigger>
  ) : (
    content
  );
};

const PackageDetails = () => {
  const navigate = useNavigate();
  const scrollContainerRef = useKeyScroll();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [packagesData, setPackagesData] = useState([]);
  const [activePackageCount, setActivePackageCount] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalRecords, setTotalRecords] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [listLoading, setListLoading] = useState(false);
  const [filterQuery, setfilterQuery] = useState("");
  const [searchKeyword, setSearchKeyword] = useState("");
  const [sortConfig, setSortConfig] = useState({ key: null, direction: 'ascending' });
  const [isSearchFocused, setIsSearchFocused] = useState(false);
  const [selectedStatus, setSelectedStatus] = useState("All");

  useEffect(() => {
    const fetchPackageData = () => {
      setListLoading(true);
      const payload = {
        status: selectedStatus === "All" ? "" : selectedStatus.toUpperCase().replace(" ", ""),
        pageNumber: currentPage,
        pageSize: rowsPerPage,
      };

      if (sortConfig.key) {
        payload.orderBy = sortConfig.key;
        payload.orderByType = sortConfig.direction === "ascending" ? "ASC" : "DESC";
      }

      if (searchKeyword) {
        payload.filterQuery = searchKeyword;
      }

      Instance.post("/user/credit/list", payload)
        .then((res) => {
          const dashMsg = res.data?.message;
          const packages = Array.isArray(dashMsg) ? dashMsg : [];
          setPackagesData(packages);
          setTotalPages(res.data?.totalPages || 1);
          setTotalRecords(res.data?.totalRecords || res.data?.totalRecord || 0);
          setActivePackageCount(packages.length);
          setError("");
        })
        .catch((err) => {
          setError("Cannot fetch package details");
          console.error("Failed to fetch dashboard data", err);
        })
        .finally(() => {
          setListLoading(false);
          setLoading(false);
        });
    };

    fetchPackageData();
  }, [currentPage, rowsPerPage, sortConfig, searchKeyword, selectedStatus]);

  const formatDate = (dateString, format = 'short') => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString.replace(' ', 'T'));
    if (isNaN(date.getTime())) return dateString;

    if (format === 'custom-bullet') {
      const datePart = date.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
      const timePart = date.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true });
      return `${datePart} • ${timePart}`;
    }

    const options = format === 'long'
      ? { year: 'numeric', month: 'short', day: '2-digit', hour: '2-digit', minute: '2-digit' }
      : { year: 'numeric', month: 'short', day: '2-digit' };

    return date.toLocaleDateString('en-US', options);
  };


  const purchaseCredits = (packageId) => {
    Instance.post("/user/credit/buyCredits", { packageId })
      .then((response) => {
        if (response.data.resCode === "200") {
          const superPNR = response.data.superPNR;
          if (superPNR) localStorage.setItem("superPNR", superPNR);
          window.open(`/customer/pricing/${packageId}`, "_blank");
        }
      })
      .catch((error) => console.error("Cannot proceed with buying credits", error));
  };

  const handleSearchChange = (e) => {
    const value = e.target.value.toLowerCase();
    setfilterQuery(value);
    if (!value) {
      setSearchKeyword("");
      setCurrentPage(1);
    }
  };

  const handleSearchSubmit = () => {
    setCurrentPage(1);
    setSearchKeyword(filterQuery);
  };

  const handleSearchKeyDown = (e) => {
    if (e.key === "Enter") {
      handleSearchSubmit();
    }
  };

  const statusOptions = [
    { value: "All", label: "All" },
    { value: "Active", label: "Active" },
    { value: "Pending", label: "Pending" },
    { value: "Expired", label: "Expired" },
    { value: "Payment Failed", label: "Payment Failed" },
  ];

  const handleStatusChange = (selectedOption) => {
    const status = selectedOption ? selectedOption.value : "All";
    setSelectedStatus(status);
    setCurrentPage(1);
  };

  const statusSelectStyles = {
    control: (base, state) => ({
      ...base,
      background: '#f4f5f7',
      borderRadius: '30px',
      border: state.isFocused ? '1px solid #595fde' : '1px solid #dfe1e6',
      height: '38px',
      minHeight: '38px',
      boxShadow: 'none',
      cursor: 'pointer',
      width: '180px',
      '&:hover': {
        borderColor: '#595fde',
        background: '#ffffff',
      }
    }),
    valueContainer: (base) => ({
      ...base,
      padding: '0 16px',
    }),
    singleValue: (base) => ({
      ...base,
      fontSize: '13px',
      color: '#444',
      fontWeight: '600',
    }),
    placeholder: (base) => ({
      ...base,
      fontSize: '13px',
      color: '#444',
      fontWeight: '600',
    }),
    indicatorsContainer: (base) => ({
      ...base,
      paddingRight: '8px',
    }),
    indicatorSeparator: () => ({
      display: 'none',
    }),
    menu: (base) => ({
      ...base,
      marginTop: '0',
      borderRadius: '12px',
      overflow: 'hidden',
      boxShadow: '0 4px 20px rgba(0,0,0,0.1)',
      border: '1px solid #dfe1e6',
      zIndex: 10,
    }),
    option: (base, state) => ({
      ...base,
      fontSize: '13px',
      fontWeight: '500',
      padding: '10px 16px',
      cursor: 'pointer',
      backgroundColor: state.isSelected ? '#595fde' : state.isFocused ? '#f1f2fd' : 'transparent',
      color: state.isSelected ? '#ffffff' : '#444',
      '&:active': {
        backgroundColor: '#595fde',
        color: '#ffffff',
      }
    })
  };

  const renderSortIcon = (field) => {
    if (sortConfig.key !== field) {
      return (
        <FontAwesomeIcon
          icon={faArrowDown}
          style={{ marginLeft: 6, opacity: 0.3 }}
        />
      );
    }

    return (
      <FontAwesomeIcon
        icon={
          sortConfig.direction === "ascending"
            ? faArrowUp
            : faArrowDown
        }
        style={{ marginLeft: 6, opacity: 1 }}
      />
    );
  };

  const paginatedPackages = packagesData;

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  const handleRowsPerPageChange = (rows) => {
    setRowsPerPage(rows);
    setCurrentPage(1);
  };

  const handleSortChange = (selectedColumn) => {
    setSortConfig((prev) => ({
      key: selectedColumn.sortField,
      direction:
        prev.key === selectedColumn.sortField &&
          prev.direction === "ascending"
          ? "descending"
          : "ascending",
    }));

    setCurrentPage(1);
  };

  const columns = React.useMemo(() => [
    {
      name: <div style={{ display: 'flex', alignItems: 'center' }}>PACKAGE NAME {renderSortIcon('packageName')}</div>,
      selector: row => row.packageName,
      sortable: true,
      sortField: 'packageName',
      width: '250px',
      cell: (row, index) => (
        <TruncatedPackageName name={row.packageName || "N/A"} id={`tooltip-table-pkg-${index}`} maxLines={2} isTitle={false} />
      ),
    },
    {
      name: <div style={{ display: 'flex', alignItems: 'center' }}>PR LIMIT {renderSortIcon('prLimit')}</div>,
      selector: row => row.prLimit,
      sortable: true,
      sortField: 'prLimit',
      center: true,
      width: '120px',
    },
    {
      name: <div style={{ display: 'flex', alignItems: 'center' }}>USED PR {renderSortIcon('usedPR')}</div>,
      selector: row => row.usedPR,
      sortable: true,
      sortField: 'usedPR',
      center: true,
      width: '120px',
    },
    {
      name: <div style={{ display: 'flex', alignItems: 'center' }}>CREDITS LEFT {renderSortIcon('availablePR')}</div>,
      selector: row => row.availablePR,
      sortable: true,
      sortField: 'availablePR',
      center: true,
      width: '150px',
    },
    {
      name: <div style={{ display: 'flex', alignItems: 'center' }}>EXPIRY {renderSortIcon('expiryDate')}</div>,
      selector: row => row.expiryDate,
      sortable: true,
      sortField: 'expiryDate',
      center: true,
      width: '180px',
      cell: row => formatDate(row.expiryDate, 'short'),
    },
    {
      name: <div style={{ display: 'flex', alignItems: 'center' }}>PRICE {renderSortIcon('price')}</div>,
      selector: row => row.price,
      sortable: true,
      sortField: 'price',
      width: '120px',
      cell: row => (
        <div style={{ display: 'flex', alignItems: 'center' }}>
          {row.price != null && row.price !== '' ? `$${row.price}` : 'N/A'}
        </div>
      ),
    },
    {
      name: <div style={{ display: 'flex', alignItems: 'center' }}>STATUS {renderSortIcon('status')}</div>,
      selector: row => row.status,
      sortable: true,
      sortField: 'status',
      center: true,
      width: '160px',
      cell: row => (
        <span
          className={`status-badge ${row.status === "ACTIVE" ? "status-active" : "status-inactive"
            }`}
        >
          {row.status ? row.status.toLowerCase() : 'N/A'}
        </span>
      ),
    },
    {
      name: 'BUY AGAIN',
      center: true,
      width: '150px',
      cell: row => row.status === "ACTIVE" && row.showOnPricing === "true" ? (
        <Fillbutton
          text="Buy Again"
          className="small_btn package-details-buy-again-btn"
          onClick={() => purchaseCredits(row.packageId)}
        />
      ) : null,
    },
  ], [sortConfig]);

  const customStyles = {
    table: {
      style: {
        minWidth: 'max-content',
      },
    },
    headRow: {
      style: {
        backgroundColor: '#f8f9fa',
        // borderBottom: '2px solid #f0f0f0',
        minHeight: '52px',
        minWidth: 'max-content',
      },
    },
    headCells: {
      style: {
        fontSize: '13px',
        textTransform: 'uppercase',
        fontWeight: '700',
        color: '#444444',
        flexShrink: 0,
        borderRight: 'none',
      },
    },
    cells: {
      style: {
        color: '#6c757d',
        fontSize: '14px',
        fontWeight: '500',
        whiteSpace: 'normal',
        flexShrink: 0,
        borderRight: 'none',
      },
    },
    rows: {
      style: {
        minHeight: '70px',
        minWidth: 'max-content',
      },
      highlightOnHoverStyle: {
        backgroundColor: '#f5f6ff',
        outline: 'none',
        transition: 'background-color 0.2s ease',
      },
    },
  };

  if (loading) return <PremiumLoader fullPage={false} text="Preparing package details..." />;
  if (error) return <div className="alert alert-danger m-4">{error}</div>;

  return (
    <div className="package-details-content-wrapper">
      <div className="package-details-header-row">
        <h2 className="package-details-header-title">Package Details</h2>
        <div className="package-details-header-actions">
          <div className="package-details-status-filter-wrapper">
            <Select
              options={statusOptions}
              value={statusOptions.find(opt => opt.value === selectedStatus)}
              onChange={handleStatusChange}
              styles={statusSelectStyles}
              isSearchable={false}
              placeholder="Select Status"
            />
          </div>
          <div className="package-details-search-bar" style={{ border: `1px solid ${isSearchFocused ? '#595fde' : '#dfe1e6'}` }}>
            <img
              src="/build/img/search.svg"
              alt="search"
              width="16"
              className="me-2 opacity-50"
              style={{ cursor: "pointer" }}
              onClick={handleSearchSubmit}
            />
            <input
              type="search"
              className="package-details-search-input"
              value={filterQuery}
              onChange={handleSearchChange}
              onKeyDown={handleSearchKeyDown}
              onFocus={() => setIsSearchFocused(true)}
              onBlur={() => setIsSearchFocused(false)}
              placeholder="Search Packages"
            />
          </div>
        </div>
      </div>

      <div className="package-details-table-container">
        <div
          className="package-details-table-scroll-wrapper"
          ref={scrollContainerRef}
          tabIndex="0"
          style={{ outline: 'none' }}
        >
          {listLoading ? (
            <div className="text-center py-5 d-flex align-items-center justify-content-center" style={{ minHeight: '400px' }}>
              <PremiumLoader fullPage={false} text="Loading packages..." />
            </div>
          ) : paginatedPackages.length === 0 ? (
            <div className="package-details-empty-state-package m-4">
              <img src="/build/img/Package.svg" alt="Package" width="48" height="48" className="mb-3 opacity-20" />
              <p className="package-details-empty-state-text">No packages found for the selected criteria</p>
              <button
                className="package-details-buy-now-btn"
                onClick={() => navigate("/customer/pricing")}
              >
                Buy Now
              </button>
            </div>
          ) : (
            <DataTable
              columns={columns}
              data={paginatedPackages}
              customStyles={customStyles}
              sortServer
              onSort={handleSortChange}
              sortIcon={<></>}
              highlightOnHover
              pointerOnHover
              dense={false}
              className="package-details-package-table-react"
            />
          )}
        </div>

        <div className="package-details-footer-pagination">
          <CustomPagination
            totalItems={totalRecords}
            totalPages={totalPages}
            rowsPerPage={rowsPerPage}
            currentPage={currentPage}
            onPageChange={handlePageChange}
            onRowsPerPageChange={handleRowsPerPageChange}
          />
        </div>
      </div>
    </div>
  );
};

export default PackageDetails;
